import { useI18n, type MessageKey } from '../i18n/index.tsx';

/**
 * The "how does a hand work" tour — the one bit of the game's own rules that
 * gets an in-app explainer, as opposed to the tray gesture coach, which
 * teaches the controls rather than Rummy itself. Originally reachable only
 * from inside a room (RoomScreen); also opened from the Lobby (App.tsx) so a
 * brand-new player is not required to create a table before they can read it.
 */
export const PRIMER_KEYS = ['draw', 'build', 'finish'] as const;

export function RulePrimer({
  step,
  onStep,
  onClose,
  onFullRules,
}: {
  step: number;
  onStep: (step: number) => void;
  onClose: () => void;
  /** The tour covers a turn; this goes to the whole game. */
  onFullRules?: () => void;
}) {
  const { t } = useI18n();
  const key = PRIMER_KEYS[step]!;
  const glyph = key === 'draw' ? '↗' : key === 'build' ? '♣' : '✓';
  const last = step === PRIMER_KEYS.length - 1;
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="rule-primer-title">
      <section className="modal__panel rule-primer">
        <p className="panel__title">{t('room.primer.eyebrow')}</p>
        <div className="rule-primer__visual" aria-hidden="true">{glyph}</div>
        <p className="rule-primer__count">{step + 1} / {PRIMER_KEYS.length}</p>
        <h2 id="rule-primer-title">{t(`room.primer.${key}.title` as MessageKey)}</h2>
        <p className="modal__note">{t(`room.primer.${key}.copy` as MessageKey)}</p>
        <div className="rule-primer__dots" aria-hidden="true">
          {PRIMER_KEYS.map((item, index) => <span key={item} className={index === step ? 'is-on' : ''} />)}
        </div>
        {onFullRules && (
          <button type="button" className="btn btn--link rule-primer__more" onClick={onFullRules}>
            {t('rules.full')}
          </button>
        )}
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>{t('room.primer.close')}</button>
          {step > 0 && <button type="button" className="btn" onClick={() => onStep(step - 1)}>{t('room.primer.back')}</button>}
          <button type="button" className="btn btn--primary" onClick={() => (last ? onClose() : onStep(step + 1))}>
            {last ? t('room.primer.done') : t('room.primer.next')}
          </button>
        </div>
      </section>
    </div>
  );
}
